import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { EditUserDialog } from '../components/EditUserDialog';
import { ContentManager } from '../components/ContentManager';
import { PacksManager } from '../components/PacksManager';
import { SiteSettingsManager } from '../components/SiteSettingsManager';
import { AccessManager } from '../components/AccessManager';
import { NotificationManager } from '../components/NotificationManager';
import { CenterManager } from '../components/CenterManager';
import { TrialPacksManager } from '../components/TrialPacksManager';
import { Users, Upload, Activity, Edit, Search, X, DollarSign, Settings, Lock, Bell, Building2, RefreshCw, Package, Gift } from 'lucide-react';
import { usersAPI, trialsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

export default function SuperAdmin() {
  const [activeTab, setActiveTab] = useState('centers');
  const { updateUserInfo } = useAuth();
  const [editingUser, setEditingUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [assignModal, setAssignModal] = useState({ open: false, userId: '', userName: '', amount: 10, packId: '' });
  const [assigning, setAssigning] = useState(false);
  const [trialPacks, setTrialPacks] = useState([]);

  // Load users from API
  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await usersAPI.getAll();
      setUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
    // Load trial packs for the assign modal
    fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/trials/packs`)
      .then(r => r.json()).then(d => setTrialPacks(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  const activeUsers = users.filter(u => !u.is_archived);
  const archivedUsers = users.filter(u => u.is_archived);

  // Helper functions
  const isPackActive = (finAbonnement) => {
    if (!finAbonnement) return false;
    return new Date(finAbonnement) > new Date();
  };

  // Calculate statistics
  const packsActive = activeUsers.filter(u => isPackActive(u.fin_abonnement)).length;
  const packsExpired = activeUsers.filter(u => !isPackActive(u.fin_abonnement)).length;

  // Filter users based on search query
  const filterUsers = (userList) => {
    if (!searchQuery.trim()) return userList;
    
    const query = searchQuery.toLowerCase();
    return userList.filter(user => {
      const nom = (user.nom || '').toLowerCase();
      const email = (user.email || user.username || '').toLowerCase();
      const telephone = (user.telephone || '').toLowerCase();
      
      return nom.includes(query) || email.includes(query) || telephone.includes(query);
    });
  };

  const filteredActiveUsers = filterUsers(activeUsers);
  const filteredArchivedUsers = filterUsers(archivedUsers);

  const handleArchiveUser = async (userId) => {
    try {
      const result = await usersAPI.archive(userId);
      if (result.success) {
        toast.success('Utilisateur archivé');
        loadUsers(); // Reload users
      } else {
        toast.error(result.message || 'Erreur lors de l\'archivage');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'archivage');
    }
  };

  const handleUnarchiveUser = async (userId) => {
    try {
      const result = await usersAPI.restore(userId);
      if (result.success) {
        toast.success('Utilisateur réactivé');
        loadUsers(); // Reload users
      } else {
        toast.error(result.message || 'Erreur lors de la réactivation');
      }
    } catch (error) {
      toast.error('Erreur lors de la réactivation');
    }
  };

  const handleSaveUserEdit = async (userId, updates) => {
    try {
      const result = await usersAPI.update(userId, updates);
      if (result.success) {
        setEditingUser(null);
        toast.success('Informations mises à jour');
        loadUsers(); // Reload users
      } else {
        toast.error(result.message || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleAssignTrials = async () => {
    const amount = parseInt(assignModal.amount, 10);
    if (!amount || amount <= 0) { toast.error('Montant invalide'); return; }
    setAssigning(true);
    try {
      const result = await trialsAPI.assignTrials(assignModal.userId, amount);
      if (result.success) {
        toast.success(result.message || `${amount} essais attribués`);
        setAssignModal({ open: false, userId: '', userName: '', amount: 10, packId: '' });
        loadUsers();
      } else {
        toast.error(result.message || 'Erreur');
      }
    } catch {
      toast.error('Erreur lors de l\'attribution');
    } finally {
      setAssigning(false);
    }
  };

  const handleUploadContent = (e) => {
    e.preventDefault();
    if (!uploadTitle || !uploadDescription) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    const content = {
      title: uploadTitle,
      description: uploadDescription,
      file_name: uploadFileName || 'Document',
      file_url: '#'
    };

    addModuleContent(uploadModule, content);
    toast.success('Contenu ajouté avec succès');
    setUploadTitle('');
    setUploadDescription('');
    setUploadFileName('');
  };

  const handleDeleteContent = (moduleName, contentId) => {
    deleteModuleContent(moduleName, contentId);
    toast.success('Contenu supprimé');
  };

  const tabs = [
    { id: 'centers', label: 'Centres Linguistiques', icon: Building2 },
    { id: 'users', label: 'Gestion des utilisateurs', icon: Users },
    { id: 'content', label: 'Gestion de contenus', icon: Upload },
    { id: 'trial-packs', label: 'Packs d\'Essais', icon: Package },
    { id: 'access', label: 'Gestion des accès', icon: Lock },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'packs', label: 'Gestion des packs', icon: Activity },
    { id: 'settings', label: 'Paramètres du site', icon: Settings },
  ];

  return (
    <Layout>
      <div className="space-y-8 animate-in">
        <div className="space-y-2">
          <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold tracking-tight">Panel Super Administrateur</h1>
          <p className="text-base text-muted-foreground">Gestion complète de la plateforme TCF Canada</p>
        </div>

        {/* Tabs */}
        <div className="overflow-x-auto">
          <div className="flex min-w-max space-x-2 border-b">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  data-testid={`tab-${tab.id}`}
                  className={`flex items-center space-x-2 px-4 py-3 border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-primary text-primary font-medium'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Centers Tab */}
        {activeTab === 'centers' && <CenterManager />}

        {/* Users Tab - same as AdminControl but without the exercise link */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            {/* Search bar */}
            <div className="bg-card p-4 rounded-xl border">
              <div className="flex items-center space-x-3">
                <Search className="h-5 w-5 text-muted-foreground" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="user-search-input"
                  placeholder="Rechercher par nom, email ou téléphone..."
                  className="flex-1 h-10 px-3 rounded-md border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    data-testid="clear-search-button"
                    className="p-2 rounded-lg hover:bg-secondary transition-colors"
                    title="Effacer la recherche"
                  >
                    <X className="h-5 w-5 text-muted-foreground" />
                  </button>
                )}
              </div>
              {searchQuery && (
                <p className="text-sm text-muted-foreground mt-2">
                  {filteredActiveUsers.length + filteredArchivedUsers.length} résultat(s) trouvé(s)
                </p>
              )}
            </div>

            {/* Legend */}
            <div className="bg-card p-4 rounded-xl border">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium">Légende des indicateurs</h3>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={loadUsers}
                    className="flex items-center space-x-1 text-sm text-muted-foreground hover:text-primary"
                    title="Actualiser"
                  >
                    <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                    <span>Actualiser</span>
                  </button>
                  <div className="text-sm font-medium text-primary">
                    Total utilisateurs inscrits : <span className="text-lg">{users.length}</span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2">
                    <div className="h-3 w-3 rounded-full bg-green-500" />
                    <span className="text-muted-foreground">Utilisateurs actifs</span>
                  </div>
                  <span className="px-2 py-1 rounded-full bg-green-500/10 text-green-600 font-medium text-xs">
                    {activeUsers.length}
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2">
                    <div className="h-3 w-3 rounded-full bg-gray-400" />
                    <span className="text-muted-foreground">Utilisateurs archivés</span>
                  </div>
                  <span className="px-2 py-1 rounded-full bg-gray-400/10 text-gray-600 font-medium text-xs">
                    {archivedUsers.length}
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2">
                    <div className="h-3 w-3 rounded-full bg-blue-500" />
                    <span className="text-muted-foreground">Pack actif (abonnement valide)</span>
                  </div>
                  <span className="px-2 py-1 rounded-full bg-blue-500/10 text-blue-600 font-medium text-xs">
                    {packsActive}
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2">
                    <div className="h-3 w-3 rounded-full bg-red-500" />
                    <span className="text-muted-foreground">Pack expiré</span>
                  </div>
                  <span className="px-2 py-1 rounded-full bg-red-500/10 text-red-600 font-medium text-xs">
                    {packsExpired}
                  </span>
                </div>
              </div>
            </div>

            {/* User tables - same as AdminControl */}
            <div>
              <h2 className="text-2xl font-semibold mb-4">Utilisateurs actifs</h2>
              <div className="bg-card rounded-xl border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="active-users-table">
                    <thead className="bg-secondary">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-medium">Statut</th>
                        <th className="px-6 py-4 text-left text-sm font-medium">Nom</th>
                        <th className="px-6 py-4 text-left text-sm font-medium">Email</th>
                        <th className="px-6 py-4 text-left text-sm font-medium">Téléphone</th>
                        <th className="px-6 py-4 text-left text-sm font-medium">Pays</th>
                        <th className="px-6 py-4 text-left text-sm font-medium">Pack</th>
                        <th className="px-6 py-4 text-left text-sm font-medium">Essais IA</th>
                        <th className="px-6 py-4 text-left text-sm font-medium">Fin abonnement</th>
                        <th className="px-6 py-4 text-right text-sm font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {filteredActiveUsers.length === 0 ? (
                        <tr>
                          <td colSpan="9" className="px-6 py-8 text-center text-muted-foreground">
                            {searchQuery ? 'Aucun utilisateur trouvé' : 'Aucun utilisateur actif'}
                          </td>
                        </tr>
                      ) : (
                        filteredActiveUsers.map((user) => {
                          const packActive = isPackActive(user.fin_abonnement);
                          
                          return (
                            <tr key={user.id} data-testid={`user-row-${user.id}`}>
                              <td className="px-6 py-4">
                                <div className="flex items-center space-x-2">
                                  <div 
                                    className={`h-3 w-3 rounded-full ${packActive ? 'bg-blue-500' : 'bg-red-500'}`}
                                    data-testid={`pack-indicator-${user.id}`}
                                    title={packActive ? 'Pack actif' : 'Pack expiré'}
                                  />
                                </div>
                              </td>
                              <td className="px-6 py-4 text-sm font-medium">{user.nom || 'Non renseigné'}</td>
                              <td className="px-6 py-4 text-sm">{user.email || user.username}</td>
                              <td className="px-6 py-4 text-sm text-muted-foreground">{user.telephone || '-'}</td>
                              <td className="px-6 py-4 text-sm text-muted-foreground">{user.pays || '-'}</td>
                              <td className="px-6 py-4 text-sm">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  user.type_pack === 'Premium' || user.type_pack === 'VIP' ? 'bg-primary/20 text-primary' :
                                  user.type_pack === 'Standard' ? 'bg-green-500/20 text-green-600' :
                                  'bg-secondary text-secondary-foreground'
                                }`}>
                                  {user.type_pack || 'Gratuit'}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-sm">{user.credits_ia}</td>
                              <td className="px-6 py-4 text-sm">
                                <span className={packActive ? 'text-foreground' : 'text-destructive font-medium'}>
                                  {user.fin_abonnement ? new Date(user.fin_abonnement).toLocaleDateString('fr-FR') : '-'}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-right">
                                <div className="flex items-center justify-end space-x-2">
                                  {(user.role === 'admin' || user.role === 'super_admin') && (
                                    <button
                                      onClick={() => setAssignModal({ open: true, userId: user.id, userName: user.nom || user.email || user.username, amount: 10 })}
                                      title="Attribuer des essais"
                                      className="p-2 rounded-lg bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500 hover:text-white transition-colors"
                                    >
                                      <Gift className="h-4 w-4" />
                                    </button>
                                  )}
                                  <button
                                    onClick={() => setEditingUser(user)}
                                    data-testid={`edit-user-${user.id}`}
                                    className="p-2 rounded-lg bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground transition-colors"
                                  >
                                    <Edit className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => handleArchiveUser(user.id)}
                                    data-testid={`archive-${user.id}`}
                                    className="px-3 py-1 rounded-lg bg-destructive/10 text-destructive hover:bg-destructive hover:text-destructive-foreground text-sm transition-colors"
                                  >
                                    Archiver
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {archivedUsers.length > 0 && (
              <div>
                <h2 className="text-2xl font-semibold mb-4">Utilisateurs archivés</h2>
                <div className="bg-card rounded-xl border overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="archived-users-table">
                      <thead className="bg-secondary">
                        <tr>
                          <th className="px-6 py-4 text-left text-sm font-medium">Nom</th>
                          <th className="px-6 py-4 text-left text-sm font-medium">Email</th>
                          <th className="px-6 py-4 text-left text-sm font-medium">Rôle</th>
                          <th className="px-6 py-4 text-right text-sm font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {filteredArchivedUsers.length === 0 ? (
                          <tr>
                            <td colSpan="4" className="px-6 py-8 text-center text-muted-foreground">
                              {searchQuery ? 'Aucun utilisateur archivé trouvé' : 'Aucun utilisateur archivé'}
                            </td>
                          </tr>
                        ) : (
                          filteredArchivedUsers.map((user) => (
                            <tr key={user.id}>
                              <td className="px-6 py-4 text-sm opacity-60">{user.nom || 'Non renseigné'}</td>
                              <td className="px-6 py-4 text-sm opacity-60">{user.email || user.username}</td>
                              <td className="px-6 py-4 text-sm opacity-60">{user.role}</td>
                              <td className="px-6 py-4 text-right">
                                <button
                                  onClick={() => handleUnarchiveUser(user.id)}
                                  data-testid={`unarchive-${user.id}`}
                                  className="px-3 py-1 rounded-lg bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground text-sm transition-colors"
                                >
                                  Réactiver
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Comprehension Orale Tab - removed (now in ContentManager) */}

        {/* Content Manager Tab */}
        {activeTab === 'content' && <ContentManager />}

        {/* Trial Packs Manager Tab */}
        {activeTab === 'trial-packs' && <TrialPacksManager />}

        {/* Access Manager Tab */}
        {activeTab === 'access' && <AccessManager />}

        {/* Notifications Manager Tab */}
        {activeTab === 'notifications' && <NotificationManager />}

        {/* Packs Manager Tab */}
        {activeTab === 'packs' && <PacksManager />}

        {/* Site Settings Tab */}
        {activeTab === 'settings' && <SiteSettingsManager />}
      </div>

      {/* Edit User Dialog */}
      {editingUser && (
        <EditUserDialog
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSave={handleSaveUserEdit}
        />
      )}

      {/* Assign Trials Modal */}
      {assignModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-card rounded-2xl border shadow-2xl w-full max-w-sm">
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <Gift className="h-5 w-5 text-emerald-500" />
                Attribuer des essais IA
              </h3>
              <button onClick={() => setAssignModal({ open: false, userId: '', userName: '', amount: 10, packId: '' })}
                className="p-2 rounded-lg hover:bg-secondary transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-muted-foreground">
                Attribution à <span className="font-semibold text-foreground">{assignModal.userName}</span>
              </p>

              {/* Pack selector */}
              {trialPacks.length > 0 && (
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Attribuer via un pack</label>
                  <select
                    value={assignModal.packId}
                    onChange={(e) => {
                      const pack = trialPacks.find(p => p.id === e.target.value);
                      setAssignModal(m => ({
                        ...m,
                        packId: e.target.value,
                        amount: pack ? pack.essais_count : m.amount
                      }));
                    }}
                    className="w-full h-11 px-4 rounded-xl border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    <option value="">— Saisie manuelle —</option>
                    {trialPacks.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.name} — {p.essais_count} essais
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Manual / auto-filled amount */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium">
                  Nombre d'essais
                  {assignModal.packId && <span className="ml-2 text-xs text-emerald-500 font-normal">(défini par le pack)</span>}
                </label>
                <input
                  type="number"
                  min="1"
                  value={assignModal.amount}
                  onChange={(e) => setAssignModal(m => ({ ...m, amount: e.target.value, packId: '' }))}
                  className="w-full h-11 px-4 rounded-xl border bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                />
                <p className="text-xs text-muted-foreground">
                  Ces essais s'ajouteront au solde actuel de l'administrateur.
                </p>
              </div>

              <div className="flex gap-3 pt-2">
                <button onClick={() => setAssignModal({ open: false, userId: '', userName: '', amount: 10, packId: '' })}
                  className="flex-1 h-11 rounded-xl border font-medium hover:bg-secondary transition-colors text-sm">
                  Annuler
                </button>
                <button onClick={handleAssignTrials} disabled={assigning}
                  className="flex-1 h-11 rounded-xl bg-emerald-500 text-white font-semibold hover:bg-emerald-600 transition-colors disabled:opacity-50 text-sm">
                  {assigning ? 'Attribution...' : `Attribuer ${assignModal.amount} essais`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
